<template>
  <div v-if="isVisible" class="invitation-modal-overlay" @click.self="closeModal">
    <div class="invitation-modal">
      <div class="modal-header">
        <h2>{{ $t('invitation.sendInvitation') }}</h2>
        <button class="close-button" @click="closeModal" :aria-label="$t('common.close')">&times;</button>
      </div>

      <div class="modal-content">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="email">{{ $t('invitation.emailAddress') }} <span class="required">*</span></label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              placeholder="friend@example.com"
              required
              :disabled="loading"
              class="form-control"
            />
          </div>

          <div class="form-group">
            <label for="name">{{ $t('invitation.nameOptional') }}</label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              :placeholder="$t('invitation.friendNamePlaceholder')"
              :disabled="loading"
              class="form-control"
            />
          </div>

          <div class="form-group">
            <label for="message">{{ $t('invitation.personalMessageOptional') }}</label>
            <textarea
              id="message"
              v-model="formData.message"
              rows="3"
              :placeholder="$t('invitation.personalMessagePlaceholder')"
              :disabled="loading"
              class="form-control"
            ></textarea>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="loading">
              {{ $t('invitation.cancel') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="loading || !isFormValid">
              {{ loading ? $t('invitation.sending') : $t('invitation.sendInvitationBtn') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useInvitationStore } from '../stores/invitation';

export default {
  name: 'InvitationModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const invitationStore = useInvitationStore();

    const formData = ref({
      email: '',
      name: '',
      message: ''
    });

    const loading = ref(false);
    const error = ref(null);
    const successMessage = ref(null);

    const isFormValid = computed(() => {
      return formData.value.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email);
    });

    const handleSubmit = async () => {
      console.log('[DEBUG] InvitationModal.handleSubmit: Starting');
      console.log('[DEBUG] InvitationModal.handleSubmit: formData:', formData.value);
      console.log('[DEBUG] InvitationModal.handleSubmit: invitationStore type:', typeof invitationStore);
      console.log('[DEBUG] InvitationModal.handleSubmit: invitationStore.sendInvitation type:', typeof invitationStore?.sendInvitation);
      
      // Check authentication state before proceeding
      const authCheck = {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        user: localStorage.getItem('user')
      };
      console.log('[DEBUG] InvitationModal.handleSubmit: Authentication state check:', {
        accessTokenPresent: !!authCheck.accessToken,
        refreshTokenPresent: !!authCheck.refreshToken,
        userPresent: !!authCheck.user,
        accessTokenPreview: authCheck.accessToken ? authCheck.accessToken.substring(0, 20) + '...' : null,
        localStorageKeys: Object.keys(localStorage).filter(key => key.toLowerCase().includes('token') || key === 'user')
      });
      
      error.value = null;
      successMessage.value = null;

      if (!isFormValid.value) {
        error.value = 'Please enter a valid email address';
        return;
      }

      loading.value = true;

      try {
        if (!invitationStore || typeof invitationStore.sendInvitation !== 'function') {
          console.error('[DEBUG] InvitationModal.handleSubmit: invitationStore.sendInvitation is not a function!');
          throw new Error('Invitation store is not properly initialized');
        }
        
        console.log('[DEBUG] InvitationModal.handleSubmit: Calling invitationStore.sendInvitation');
        const result = await invitationStore.sendInvitation({
          email: formData.value.email,
          name: formData.value.name || undefined,
          message: formData.value.message || undefined
        });
        console.log('[DEBUG] InvitationModal.handleSubmit: Result received:', result);

        if (result.success) {
          console.log('[DEBUG] InvitationModal.handleSubmit: Success!');
          
          // Check if email was queued
          if (result.emailQueued || result.queuePosition) {
            successMessage.value = `Invitation created! Email is queued (position #${result.queuePosition}). It will be sent automatically when quota is available.`;
            
            // If invitation code is provided, show it so user can share manually
            if (result.invitationCode) {
              successMessage.value += `\n\nInvitation code: ${result.invitationCode}\n(You can share this code manually if needed)`;
            }
          } else {
            successMessage.value = `Invitation sent successfully to ${formData.value.email}!`;
          }
          
          // Reset form
          formData.value = {
            email: '',
            name: '',
            message: ''
          };

          // Emit success event
          emit('success', result);

          // Close modal after 3 seconds (longer if queued to give user time to read)
          setTimeout(() => {
            closeModal();
          }, result.emailQueued ? 5000 : 2000);
        } else {
          console.warn('[DEBUG] InvitationModal.handleSubmit: Result success is false:', result);
          
          // Check if quota exhausted and queued
          if (result.quotaExhausted && result.queued) {
            successMessage.value = `Invitation created! Email is queued (position #${result.queuePosition}). It will be sent automatically when quota is available.`;
            
            if (result.invitationCode) {
              successMessage.value += `\n\nInvitation code: ${result.invitationCode}\n(You can share this code manually if needed)`;
            }
          } else {
            error.value = result.error || 'Failed to send invitation. Please try again.';
          }
        }
      } catch (err) {
        console.error('[DEBUG] InvitationModal.handleSubmit: Error caught:', err);
        console.error('[DEBUG] InvitationModal.handleSubmit: Error type:', typeof err);
        console.error('[DEBUG] InvitationModal.handleSubmit: Error message:', err?.message);
        console.error('[DEBUG] InvitationModal.handleSubmit: Error stack:', err?.stack);
        error.value = err.message || 'Failed to send invitation. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const closeModal = () => {
      error.value = null;
      successMessage.value = null;
      formData.value = {
        email: '',
        name: '',
        message: ''
      };
      emit('close');
    };

    return {
      formData,
      loading,
      error,
      successMessage,
      isFormValid,
      handleSubmit,
      closeModal
    };
  }
};
</script>

<style scoped>
.invitation-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.invitation-modal {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-button:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-content {
  padding: 25px;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.required {
  color: #e74c3c;
}

.form-control {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1em;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 25px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 1em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background: #e0e0e0;
}

.alert {
  padding: 12px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
}

.alert-success {
  background: #efe;
  border: 1px solid #cfc;
  color: #3c3;
}
</style>
